import React, { FC, useContext, useEffect, useState } from "react";
import { ServiceSection } from "../../../../models/manageWebSite";
import { MobXProviderContext } from "mobx-react";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { getErrorTitle } from "../../../../constants/constants";
import { Button } from "primereact/button";
import { Controller, useForm } from "react-hook-form";
import { RadioButton } from "primereact/radiobutton";
import { InputText } from "primereact/inputtext";
import { normalizePlainText } from "../../../../utils/securityUtils";
import {
  appAccentPanelStyle,
  appDialogContentStyle,
  appDialogHeaderStyle,
  appDialogStyle,
  appFormSectionStyle,
  appMutedTextStyle,
  renderDialogFooter,
  renderSurfaceHeader,
} from "../../../../components/general/uiSurfaceStyles";

interface EditModalProps {
  data: ServiceSection | null;
  isOpen: boolean;
  toggleModal: (open: boolean) => void;
  flag: boolean;
  setFlag: (flag: boolean) => void;
  homeId: string | null;
}

const EditModal: FC<EditModalProps> = ({
  isOpen,
  toggleModal,
  flag,
  setFlag,
  data,
  homeId,
}) => {
  const toast = React.useRef<Toast>(null);
  const { backendAPIStore } = useContext(MobXProviderContext);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loadingImg, setLoadingImg] = useState<boolean>(false);
  const [updatedData, setUpdatedData] = useState<ServiceSection | null>(data);
  const [services, setServices] = useState<any[]>(data?.services || []);
  const [activities, setActivities] = useState<any[]>(data?.activities || []);

  const defaultValues = {
    newAction: "",
    isService: true,
  }

  const { formState: { errors }, handleSubmit, reset, control, watch } = useForm({ defaultValues });

  useEffect(() => {
    setUpdatedData(data);
    setServices(data?.services || []);
    setActivities(data?.activities || []);
  }, [data, isOpen]);

  const isServiceSelected = watch("isService");

  const onSubmit = async (data: any) => {
    let updatedServices = services;
    let updatedActivities = activities;
    if (data.isService) {
      if (data.newAction && data.newAction.trim() !== "") {
        updatedServices = [...updatedServices, normalizePlainText(data.newAction, { maxLength: 140 })];
      }
    } else {
      if (data.newAction && data.newAction.trim() !== "") {
        updatedActivities = [...updatedActivities, normalizePlainText(data.newAction, { maxLength: 140 })];
      }
    }

    setErrorMessage(null);
    setLoadingImg(true);
    try {
      await backendAPIStore.init();
      const { ...apiData } = {
        ...updatedData,
        services: updatedServices,
        activities: updatedActivities,
        modifiedAt: new Date().toISOString(),
        description: data.description,
      };

      await backendAPIStore.updateTemplateSection(apiData);

    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: 'Failed to upload image', life: 3000 });
    } finally {
      reset();
      toggleModal(false);
      setFlag(!flag);
      setLoadingImg(false);
    }
  };

  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>
  };

  const headerElement = renderSurfaceHeader(
    "Manage Services And Activities",
    "Add or remove service and activity content that appears on the website.",
    "pi pi-list",
  );

  const footerContent = renderDialogFooter({
    errorMessage,
    loading: loadingImg,
    onCancel: () => toggleModal(false),
    onSubmit: handleSubmit(onSubmit),
    submitLabel: "Save Section",
  });

  const handleDeleteService = (index: number) => {
    const updatedServices = services.filter((_: any, idx: number) => idx !== index);
    setServices(updatedServices);
  }

  const handleDeleteActivity = (index: number) => {
    const updatedActivities = activities.filter((_: any, idx: number) => idx !== index);
    setActivities(updatedActivities);
  }

  return (
    <Dialog visible={isOpen} modal header={headerElement} footer={footerContent} style={appDialogStyle}
      headerStyle={appDialogHeaderStyle} contentStyle={appDialogContentStyle}
      onHide={() => { if (!isOpen) return; toggleModal(false); }}>
      <Toast ref={toast} />
      <form>
        <div style={appAccentPanelStyle} className="mb-3">
          <div style={{ fontWeight: 700, color: "#9a3412" }}>Content manager</div>
          <div className="mt-2" style={appMutedTextStyle}>
            Add a new entry and decide whether it belongs under services or activities.
          </div>
        </div>
        <div style={appFormSectionStyle}>
          <div className="p-field p-fluid">
            <label htmlFor="businessName">New Entry</label>
            <Controller name="newAction" control={control} render={({ field }) => (
              <InputText id="businessName" type="text" {...field} className={errors.newAction ? "p-invalid" : ""} />
            )} />
            {getFormErrorMessage('newAction')}
          </div>
        </div>
        <div style={{ ...appFormSectionStyle, marginTop: "1rem" }} className="p-field-radiobutton">
          <Controller name="isService" control={control} render={({ field }) => (
            <div className="flex gap-3">
              <div className="flex align-items-center">
                <RadioButton type="radio" id="service" {...field} defaultChecked={true} checked={field.value === true} onChange={() => field.onChange(true)} />
                <label htmlFor="service" className="ml-2">Service</label>
              </div>
              <div className="flex align-items-center">
                <RadioButton type="radio" id="activity" {...field} value={false} checked={field.value === false} onChange={() => field.onChange(false)} />
                <label htmlFor="activity" className="ml-2">Activity</label>
              </div>
            </div>
          )} />
        </div>
      </form>
      {isServiceSelected && (
      <div style={{ ...appFormSectionStyle, marginTop: "1rem" }}>
      <h3 className="mt-0">Added Services: {services.length}</h3>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "1rem",
          alignItems: "stretch",
        }}
      >
        {
          services.length > 0 ? services.map((service, index) => (
            <div
              key={index}
              style={{
                position: "relative",
                width: "100%",
                background: "linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)",
                border: "1px solid #d9e8f5",
                borderRadius: "20px",
                padding: "1rem 3rem 1rem 1rem",
                boxShadow: "0 16px 32px rgba(15,61,99,0.08)",
                minHeight: "92px",
                display: "flex",
                alignItems: "center",
                color: "#29465b",
                fontWeight: 600,
                lineHeight: 1.6,
              }}
            >
              <div style={{ width: "100%" }}>
                {service}
              </div>
              <Button icon="pi pi-times" className="p-button-rounded p-button-danger p-button-outlined" style={{ position: "absolute", top: 10, right: 10, width: "2rem", height: "2rem" }}
                onClick={(e) => { e.preventDefault(); handleDeleteService(index) }} />
            </div>
          )) : null
        }
      </div>
      </div>
      )}
      {!isServiceSelected && (
      <div style={{ ...appFormSectionStyle, marginTop: "1rem" }}>
      <h3 className="mt-0">Added Activities: {activities.length}</h3>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "1rem",
          alignItems: "stretch",
        }}
      >
        {
          activities.length > 0 ? activities.map((activity, index) => (
            <div
              key={index}
              style={{
                position: "relative",
                width: "100%",
                background: "linear-gradient(180deg, #fffaf3 0%, #ffffff 100%)",
                border: "1px solid #f0dcc2",
                borderRadius: "20px",
                padding: "1rem 3rem 1rem 1rem",
                boxShadow: "0 16px 32px rgba(180,83,9,0.08)",
                minHeight: "92px",
                display: "flex",
                alignItems: "center",
                color: "#6c4b22",
                fontWeight: 600,
                lineHeight: 1.6,
              }}
            >
              <div style={{ width: "100%" }}>
                {activity}
              </div>
              <Button icon="pi pi-times" className="p-button-rounded p-button-danger p-button-outlined" style={{ position: "absolute", top: 10, right: 10, width: "2rem", height: "2rem" }}
                onClick={(e) => { e.preventDefault(); handleDeleteActivity(index) }} />
            </div>
          )) : null
        }
      </div>
      </div>
      )}
    </Dialog>
  );
};

export default EditModal;
